import { AnyObject } from '@xbcb/shared-types';
import { WorkOrderMilestoneName } from '@xbcb/work-order-types';
import { InvoiceStatus, ChargeCode } from '@xbcb/finance-types';
import type { UsIorContinuousBondRequest } from '@xbcb/api-gateway-client';

const getMilestoneNames = (record: AnyObject) => {
  return (
    record?.milestones.map(
      ({ name }: { name: WorkOrderMilestoneName }) => name,
    ) || []
  );
};

// checks if continuous bond was acquired successfully or not
// conditions to check:
// 1) a continuous bond acquisition request using bond bot failed and no subsequent success (ideally on a retry)
// 2) The charge bond query didn't succeed
export const continuousBondFailedFromMilestones = (
  record: AnyObject,
): boolean => {
  const milestoneNames = getMilestoneNames(record);
  return (
    milestoneNames.includes(
      WorkOrderMilestoneName.US_IOR_CONTINUOUS_BOND_REQUEST_ACQUISITION_FAILED,
    ) &&
    !milestoneNames.includes(
      WorkOrderMilestoneName.US_IOR_CONTINUOUS_BOND_REQUEST_ACQUISITION_SUCCEEDED,
    ) &&
    !milestoneNames.includes(
      WorkOrderMilestoneName.US_IOR_CONTINUOUS_BOND_REQUEST_CHARGE_BOND_SUCCEEDED,
    )
  );
};

// checks if continuous bond was requested
export const continuousBondRequestedFromMilestones = (
  record: UsIorContinuousBondRequest,
): boolean => {
  const milestoneNames = getMilestoneNames(record);
  return milestoneNames.includes(
    WorkOrderMilestoneName.US_IOR_CONTINUOUS_BOND_REQUEST_ACQUISITION_REQUESTED,
  );
};

// The subset of a continuous-bond record this predicate actually reads. Narrower
// than the full gql type so callers and tests can supply a minimal, type-checked
// object instead of casting through `unknown`.
export interface ContinuousBondChargeableRecord {
  group?: {
    invoices?:
      | {
          status?: InvoiceStatus | null;
          lines?: { type: ChargeCode }[] | null;
        }[]
      | null;
  } | null;
}

// A continuous-bond charge in one of these states is not live - the action may
// still be offered. Any other status (incl. absent) counts as a live charge.
const VOIDED_INVOICE_STATUSES: ReadonlySet<InvoiceStatus | null | undefined> =
  new Set([InvoiceStatus.PENDING_VOID, InvoiceStatus.VOID]);

// True only when we positively know no live charge exists; fails closed
// otherwise, since showing the action on an invoiced bond double-charges.
export const canChargeContinuousBond = (
  record?: ContinuousBondChargeableRecord | null,
): boolean => {
  const invoices = record?.group?.invoices;
  // Cannot tell - fail closed. Empty array differs: no invoices, safe to charge.
  if (!invoices) return false;
  const liveChargeExists = invoices.some(
    ({ lines, status }) =>
      lines &&
      lines.some((line) => line.type === ChargeCode.CONTINUOUS_BOND) &&
      !VOIDED_INVOICE_STATUSES.has(status),
  );
  return !liveChargeExists;
};

interface RecordWithContinuousBonds {
  usIor?: {
    continuousBonds?: {
      present?: {
        terminationDate?: string | null;
      } | null;
    } | null;
  } | null;
}

/**
 * Returns true if the IOR already has an open-ended active continuous bond that
 * makes a new acquisition redundant.
 *
 * The gate blocks only when the `present` bond has NO `terminationDate` — i.e. it is
 * active with no scheduled end, so acquiring another would create a duplicate. Once a
 * `terminationDate` is issued (even a *future* one), the bond is winding down and the
 * operator must be able to acquire the renewal early: bond sureties often require lead
 * time, and waiting until after the termination date risks a coverage gap. A future
 * bond is intentionally NOT considered here — a renewal still needs its own bond
 * request work order even if the bond was acquired offline.
 *
 * Used to prevent redundant acquisition requests without blocking legitimate renewals.
 * @param {RecordWithContinuousBonds} record - The bond request record containing usIor with continuousBonds data.
 * @return {boolean} Whether the IOR already has an open-ended active continuous bond.
 */
export const hasActiveContinuousBond = (
  record: RecordWithContinuousBonds | null | undefined,
): boolean => {
  const presentBond = record?.usIor?.continuousBonds?.present;
  return Boolean(presentBond && !presentBond.terminationDate);
};
